import {type Key, type Label} from '@online-offer/shared';
import {match} from 'ts-pattern';

import {groupBy} from 'ramda';

import {Nullish, sortByAsciiNumberLast} from 'shared/web';

import {DataUtilParams} from '../types/DataUtilParams';

type Feature = Key & {isHighlighted: boolean; isPremium: boolean};

/** Categories the catalogue does not prioritise are listed after the prioritised ones. */
const NO_SORT_PRIORITY = Number.MAX_SAFE_INTEGER;

export type CategorizedFeature = {
  categoryKey: string;
  label: Label | Nullish;
  features: Feature[];
};

export function getFeatures({alphaCatalogue, vehicle}: DataUtilParams) {
  if (!vehicle || !alphaCatalogue || !vehicle.features || !vehicle.features.length) {
    return [];
  }
  const featuresFromCatalogue = alphaCatalogue.find(
    (item) => item.property_name === 'feature' && item.vehicle_type === vehicle.type
  )?.keys;

  const categories = alphaCatalogue.find(
    (item) => item.property_name === 'feature_category' && item.vehicle_type === vehicle.type
  );

  if (!featuresFromCatalogue) {
    return [];
  }

  const intersectedFeaturesFromCatalogue = featuresFromCatalogue.flatMap((feature) => {
    const matchingVehicleFeature = vehicle.features!.find(
      (vehicleFeature) => vehicleFeature.key === feature.const_key
    );
    if (!matchingVehicleFeature) {
      return [];
    }
    return [
      {
        ...feature,
        isHighlighted: matchingVehicleFeature.isHighlighted,
        isPremium: matchingVehicleFeature.isPremium,
      },
    ];
  });

  const featuresWithCategories = groupBy(
    (item) => item.categories[0],
    intersectedFeaturesFromCatalogue
  );

  const findCategory = (categoryKey: string) =>
    categories?.keys.find((category) => category.const_key === categoryKey);

  const sortedCategoryKeys = [...Object.keys(featuresWithCategories)].sort((a, b) => {
    const priorityDifference =
      (findCategory(a)?.sort_priority ?? NO_SORT_PRIORITY) -
      (findCategory(b)?.sort_priority ?? NO_SORT_PRIORITY);

    return match(priorityDifference)
      .with(0, () =>
        sortByAsciiNumberLast(
          findCategory(a)?.labels[0]?.label ?? a,
          findCategory(b)?.labels[0]?.label ?? b
        )
      )
      .otherwise(() => priorityDifference);
  });

  const categoriesWithFeatures = sortedCategoryKeys.reduce<CategorizedFeature[]>(
    (acc, currentCategoryKey) => {
      const labelForCategory = findCategory(currentCategoryKey)?.labels[0];
      if (!labelForCategory) {
        return acc;
      }
      const features = featuresWithCategories[currentCategoryKey];
      if (!features) {
        return acc;
      }
      const sortedFeatures = features.sort((a, b) => {
        const isHighlightedA = a.isHighlighted;
        const isHighlightedB = b.isHighlighted;
        if (isHighlightedA && !isHighlightedB) {
          return -1;
        }
        if (!isHighlightedA && isHighlightedB) {
          return 1;
        }
        return sortByAsciiNumberLast(a.labels[0].label, b.labels[0].label);
      });
      return [
        ...acc,
        {
          categoryKey: currentCategoryKey,
          label: labelForCategory,
          features: sortedFeatures,
        },
      ];
    },
    []
  );
  return categoriesWithFeatures;
}
